import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface CartItem {
  id: string | number;
  nombre: string;
  precio: number;
  precioAntes?: number | null;
  imagen: string;
  cantidad: number;
  receta: boolean;
  categoria: string;
  presentacionId?: string;
  presentacionNombre?: string;
}

interface CartState {
  items: CartItem[];
  isOpen: boolean;
  isCheckoutOpen: boolean;
  
  // Actions
  addItem: (item: Omit<CartItem, 'cantidad'>, shouldOpen?: boolean) => void;
  removeItem: (id: string | number, presentacionId?: string) => void;
  updateQuantity: (id: string | number, cantidad: number, presentacionId?: string) => void;
  clearCart: () => void;
  toggleCart: () => void;
  openCart: () => void;
  closeCart: () => void;
  openCheckout: () => void;
  closeCheckout: () => void;
  
  // Getters
  getTotalItems: () => number;
  getTotalPrice: () => number;
  getSavings: () => number;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,
      isCheckoutOpen: false,

      addItem: (item, shouldOpen = true) => {
        set((state) => {
          const existingItem = state.items.find(
            (i) => i.id === item.id && i.presentacionId === item.presentacionId
          );
          
          if (existingItem) {
            return {
              items: state.items.map((i) =>
                i.id === item.id && i.presentacionId === item.presentacionId
                  ? { ...i, cantidad: i.cantidad + 1 }
                  : i
              ),
              isOpen: shouldOpen ? true : state.isOpen,
            };
          }
          
          return {
            items: [...state.items, { ...item, cantidad: 1 }],
            isOpen: shouldOpen ? true : state.isOpen,
          };
        });
      },

      removeItem: (id, presentacionId) => {
        set((state) => ({
          items: state.items.filter(
            (i) => !(i.id === id && i.presentacionId === presentacionId)
          ),
        }));
      },

      updateQuantity: (id, cantidad, presentacionId) => {
        if (cantidad <= 0) {
          get().removeItem(id, presentacionId);
          return;
        }
        
        set((state) => ({
          items: state.items.map((i) =>
            i.id === id && i.presentacionId === presentacionId
              ? { ...i, cantidad }
              : i
          ),
        }));
      },

      clearCart: () => {
        set({ items: [] });
      },

      toggleCart: () => {
        set((state) => ({ isOpen: !state.isOpen }));
      },

      openCart: () => {
        set({ isOpen: true });
      },

      closeCart: () => {
        set({ isOpen: false });
      },

      openCheckout: () => {
        set({ isCheckoutOpen: true, isOpen: false });
      },

      closeCheckout: () => {
        set({ isCheckoutOpen: false });
      },

      getTotalItems: () => {
        return get().items.reduce((total, item) => total + item.cantidad, 0);
      },

      getTotalPrice: () => {
        return get().items.reduce(
          (total, item) => total + item.precio * item.cantidad,
          0
        );
      },

      getSavings: () => {
        return get().items.reduce((total, item) => {
          if (item.precioAntes) {
            return total + (item.precioAntes - item.precio) * item.cantidad;
          }
          return total;
        }, 0);
      },
    }),
    {
      name: 'cart-storage',
      partialize: (state) => ({ items: state.items }),
    }
  )
);
